<template>
  <div :class="{ '__table-container': true, '__table-container-overlay': isBusy }">
    <b-table
      :id="`POINT_${point.id}`"
      :items="point.activities || []"
      :fields="fields"
      :tbody-tr-class="rowClass"
      class="mc-activity-table"
    >
      <!-- Loading -->
      <template #table-busy>
        <div class="__busy">
          <b-spinner class="align-middle mb-2" />
          <strong>{{ $t('fleetTask.activityTable.loading') }}</strong>
        </div>
      </template>

      <!-- Empty Result -->
      <template #empty>
        <div class="text-center my-4">{{ $t('fleetTask.activityTable.noData') }}</div>
      </template>

      <template v-if="canBulkComplete(point)" #head(complete)>
        <b-dropdown
          id="toggle-activity"
          class="dropdown-left"
          :disabled="isLoading || fleetTaskStatus === 'Menunggu Persetujuan'"
        >
          <template #button-content>
            <i class="more-icon icon-outline_more-vertical"></i>
          </template>
          <b-dropdown-item @click="handleBulkComplete(point)">
            {{ $t('fleetTask.activityTable.completeAll') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template #cell(delivery_order.number)="data">
        <router-link
          class="text-secondary-route-link"
          :to="`/delivery/${data.item?.delivery_order?.id || ''}`"
        >
          <div class="fw-bold">
            {{ data.value }}
          </div>
        </router-link>
        <div
          v-if="order(data.item).shipment_type === 'TRANSIT'"
          class="fw-bold mc-title-transit_flag"
        >
          {{ $t('fleetTask.activityTable.transitDo') }}
        </div>
      </template>

      <template #cell(movements)="{ item: activity }">
        <div :id="`${activity?.delivery_order?.number}__${activity.type}`" class="__arrow-buttons">
          <button :disabled="!canMove('UP', activity)" @click="handleMoveUp(activity)">
            <i class="icon-fill_arrow-triangle-up"></i>
          </button>

          <button :disabled="!canMove('DOWN', activity)" @click="handleMoveDown(activity)">
            <i class="icon-fill_arrow-triangle-down"></i>
          </button>
        </div>
      </template>

      <template #cell(activity_type)="{ item: { type } }">
        <div v-if="type == 'PICKUP'" class="__activity_badge __pickup">
          {{ $t('fleetTask.activityTable.pickup') }}
        </div>
        <div v-if="type == 'DROP'" class="__activity_badge __drop">
          {{ $t('fleetTask.activityTable.drop') }}
        </div>
      </template>

      <template #cell(completed_on)="{ value }">
        <span>{{ formatDateWithTz(value) }}</span>
      </template>

      <template #cell(delivery_order.total_weight)="{ item, value }">
        <span>{{ formatNumber(item.delivery_order.current_total_weight) }} Kg</span>
      </template>

      <template #cell(delivery_order.total_volume)="{ item, value }">
        <span>{{ formatNumber(item.delivery_order.current_total_volume) }} Cbm</span>
      </template>

      <template #cell(remove)="{ item }">
        <div
          v-if="!isLoading && fleetTaskStatus !== 'Menunggu Persetujuan'"
          v-b-tooltip.hover.bottomleft="$t('fleetTask.activityTable.removeDelivery')"
          role="button"
          class="__remove-order-button"
          @click="() => handleClickItem('remove', item)"
        >
          <i class="icon-outline_trash-2"></i>
          <!-- <span>Keluarkan Pengiriman</span> -->
        </div>
      </template>

      <template #cell(status)="{ item }">
        <template v-if="item.completed_on">
          <span class="__status-dots __completed"></span>
          <span>{{ $t('fleetTask.activityTable.processedCompleted') }}</span>
        </template>
        <template v-else>
          <span class="__status-dots __not-completed"></span>
          <span>{{ $t('fleetTask.activityTable.notProcessedYet') }}</span>
        </template>
      </template>

      <template #cell(address)>
        {{ point.address.name }}
      </template>

      <template #cell(target_pickup_delivery)="{ item }">
        {{ getTarget(item) }}
      </template>

      <template #cell(quantity)="{ item }">
        <div
          v-for="quantity in [getQuantity(item)]"
          :key="item.id"
          class="__quantity-cell"
        >
          <template v-if="quantity">
            <div class="__quantity-detail">
              <span>{{ formatNumber(quantity.qty) }} {{ quantity.uom }}</span>
              <span v-b-tooltip.hover="quantity.name" class="__quantity-name text-truncate">
                {{ quantity.name }}
              </span>
            </div>
            <span v-if="quantity.length > 1" class="__quantity-count">
              +{{ quantity.length - 1 }}
            </span>
          </template>
          <b-skeleton v-else width="80px" />
        </div>
      </template>

      <template #cell(shipment_number)="{ item }">
        {{ order(item).shipment_number }}
      </template>

      <template #cell(reference_number_internal)="{ item }">
        {{ order(item).reference_number_internal }}
      </template>

      <template #cell(reference_number)="{ item }">
        {{ order(item).reference_number }}
      </template>

      <template #cell(complete)="{ item }">
        <div
          v-if="
            item.status.raw_type === 'COMPLETED' &&
            !hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update')
          "
        >
          <a @click="handleOpenViewProof(item)">
            <i class="mc-fa icon-outline_file-text"></i>
          </a>
        </div>
        <ToggleActivityV2
          v-else
          :activity="item"
          :pointId="point.id"
          :fleetTaskStatus="fleetTaskStatus"
          :hasTmsUserGroupAccess="hasTmsUserGroupAccess"
          :isUserGroupNull="isUserGroupNull"
          :disabled="isLoading || fleetTaskStatus === 'Menunggu Persetujuan'"
          @click-item="(event) => handleClickItem(event, item, point)"
        />
      </template>

      <template #custom-foot="{ columns, items }">
        <b-tr class="__activity-footer">
          <b-th />

          <b-th :colspan="columns - calculateSpan()">
            <td>{{ $t('fleetTask.activityTable.total') }}</td>
          </b-th>

          <b-th>
            <td>{{ formatNumber(sum('current_total_weight', items)) }} Kg</td>
          </b-th>

          <b-th>
            <td>{{ formatNumber(sum('current_total_volume', items)) }} Cbm</td>
          </b-th>

          <b-th />
        </b-tr>
      </template>
    </b-table>

    <!-- Modals -->
    <TransferDeliveryOrderModal
      :delivery-order="selectedActivity.delivery_order"
      :point-id="point.id"
    />
    <RemoveDeliveryModal :delivery-order="selectedActivity.delivery_order" :point-id="point.id" />
    <CompleteActivityModalV2
      v-if="fleetTaskStatus !== 'Dibatalkan'"
      ref="complete-activity-modal-v2"
      :activity="selectedActivity"
      :pointId="point.id"
      :fleetTaskId="$route.params.id"
    />
    <ViewProofModal :activity="selectedActivity" :fleetTaskId="$route.params.id" />
    <DeliveryLoadHistoryModal :activity="selectedActivity" :fleetTaskId="$route.params.id" />
    <CancelDeliveryModal
      :activity="selectedActivity"
      :fleetTask="fleetTaskDetail"
      :isLastPoint="isLastPoint"
    />
    <RescheduleDeliveryModal
      :activity="selectedActivity"
      :fleetTask="fleetTaskDetail"
      :deliveryOrder="selectedDeliveryOrder"
      :isLastPoint="isLastPoint"
    />
    <BulkCompleteActivityModal
      ref="bulk-complete-activity-modal"
      :point="point"
      :fleetTaskId="$route.params.id"
      :isSingleComplete="isSingleComplete"
      :selectedActivity="selectedActivity"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import { sum } from '@/helpers/calculate'
import ToggleActivityV2 from '../ToggleActivity-v2.vue'
import CompleteActivityModalV2 from '../CompleteActivityModal-v2.vue'
import RemoveDeliveryModal from '../RemoveDeliveryModal.vue'
import TransferDeliveryOrderModal from '../TransferDeliveryOrderModal'
import ViewProofModal from '../ViewProof.vue'
import DeliveryLoadHistoryModal from '../DeliveryLoadHistory.vue'
import CancelDeliveryModal from '../CancelDeliveryModal.vue'
import RescheduleDeliveryModal from '../RescheduleDeliveryModal.vue'
import BulkCompleteActivityModal from '../BulkCompleteActivityModal.vue'

import * as FleetTaskActivity from '@/services/rest-api/lib/fleet-task/activity'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'

import { getFields } from './fields.js'
import toastErrors from '../../lib/constants/toast-errors'

import moment from 'moment'
import { formatDateWithTz, formatIndexDate } from '@/helpers/tzDate'
import { formatNumber } from '@/helpers/format-number.js'
moment.locale('id')

export default {
  components: {
    ToggleActivityV2,
    RemoveDeliveryModal,
    CompleteActivityModalV2,
    TransferDeliveryOrderModal,
    ViewProofModal,
    DeliveryLoadHistoryModal,
    CancelDeliveryModal,
    RescheduleDeliveryModal,
    BulkCompleteActivityModal,
  },
  mixins: [Helper],
  props: {
    sequence: {
      type: Array,
      default: () => [],
    },
    sequenceActLoad: {
      type: Array,
      default: () => [],
    },
    point: {
      type: Object,
      required: true,
    },
    missedRequiredFields: {
      type: Array,
      required: true,
    },
    hasTmsUserGroupAccess: {
      type: Boolean,
      default: false,
    },
    isUserGroupNull: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isTmsMarketing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isBusy: false,
      pendingRow: null,

      selectedActivity: {},
      selectedDeliveryOrder: {},
      isLastPoint: false,
      isSingleComplete: false,
    }
  },
  computed: {
    ...mapState('fleetTask', [
      'fleetTaskStatus',
      'selectedDos',
      'fleetTaskDetail',
      'hasDoTransitError',
      'sequence',
    ]),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    hasUserGroupFeature() {
      const hasScope = this.hasAccessAll(['tms-user-group:read'])
      const isSuperUser = this.isSuperUser()

      return hasScope && isSuperUser
    },
    fields() {
      const fields = getFields(this.$t.bind(this))
      let status = this.fleetTaskStatus
      const hasVendor = this.fleetTaskDetail?.vendor?.id ? true : false
      const isFromVendorRequest = this.fleetTaskDetail?.is_from_vendor_request
      if (
        hasVendor &&
        [
          'Perjalanan',
          'Selesai',
          'Dibatalkan',
          'Dijadwalkan',
          'Menunggu Vendor',
          'Diterima Vendor',
        ].includes(this.fleetTaskStatus)
      ) {
        status = 'hasVendor'
      }

      if (isFromVendorRequest) {
        status = 'hasVendor'
        if (['Perjalanan', 'Selesai'].includes(this.fleetTaskStatus)) {
          status = 'Perjalanan'
        }
      }

      if (this.isTmsMarketing) {
        status = 'Dibatalkan'
      }

      const finalFields = []
      if (fields[status]) {
        fields[status].forEach((field) => {
          if (field.scopes) {
            const hasAccess = this.hasAccessAll(field.scopes)
            if (!hasAccess) return
          }
          finalFields.push(field)
        })
      }

      if(this.isSamator){
        const newItem = {
          key: 'quantity',
          label: this.$t('fleetTask.activityTable.fields.quantity'),
          thClass: 'head-target',
        }

        const targetIndex = finalFields.findIndex(f=> f.key === 'target_pickup_delivery')
        if (targetIndex !== -1) {
          // Argumen: (indeks mulai, jumlah item yang dihapus, item baru)
          finalFields.splice(targetIndex + 1, 0, newItem);
        } else {
          // jika target tidak ditemukan, tambahkan di paling akhir
          finalFields.push(newItem);
        }
      }      

      return finalFields
    },
    hasVendor() {
      return this.fleetTaskDetail?.vendor?.id ? true : false
    },
    hasCustomFeatureSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
  },
  watch: {
    hasVendor(newValue) {
      if (!newValue && this.pendingMove) {
        this.executeMove(this.pendingMove.activity, this.pendingMove.direction)
        this.pendingMove = null
      }
    },
    hasDoTransitError(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.scrollToTransitError()
        })
      }
    },
  },
  mounted() {
    if (this.hasDoTransitError) {
      this.$nextTick(() => {
        this.scrollToTransitError()
      })
    }
  },
  methods: {
    formatNumber(value) {
      return formatNumber(value, 3) || '-'
    },
    formatDateWithTz,
    formatIndexDate,
    // TODO: change sum to helper
    sum(key, items) {
      return items.reduce((acc, { delivery_order }) => sum(acc, delivery_order[key]), 0)
    },
    async handleMoveUp(activity) {
      if (this.hasVendor) {
        this.pendingMove = {
          activity,
          direction: 'UP',
        }
        this.$bvModal.show('modal-change-seq-vendor')
        return
      }
      await this.executeMove(activity, 'UP')
    },

    async handleMoveDown(activity) {
      if (this.hasVendor) {
        this.pendingMove = {
          activity,
          direction: 'DOWN',
        }
        this.$bvModal.show('modal-change-seq-vendor')
        return
      }
      await this.executeMove(activity, 'DOWN')
    },

    async executeMove(activity, direction) {
      const res = await this.handleMove(activity, direction)
      if (res?.errors) return
      EventBus.$emit('refetch-sequence')
    },
    async handleMove(activity, direction) {
      this.$store.dispatch('fleetTask/setIsLoadingGlobalDelay', true)
      this.$store.dispatch('fleetTask/setIsInitial', false)
      this.isBusy = true

      const hash = `#${activity?.delivery_order?.number}__${activity.type}`
      if (this.$route.hash !== hash) this.$router.replace({ hash })

      return FleetTaskActivity.move(
        this.$route.params.id,
        this.point.id,
        activity.id,
        direction
      ).then(() => (this.isBusy = false))
    },
    canMove(direction, activity) {
      const idx = (activity?.movements || []).findIndex(
        (v) => v.toUpperCase() == direction.toUpperCase()
      )
      return idx != -1 && (!this.hasUserGroupFeature || this.fleetTaskDetail?.user_group != null)
    },
    isPopNotCompleted(sequence_id, delivery_id){
      const filteredSequence = this.sequence.filter((item)=>item.id != sequence_id)
      let isNotCompleted = false
      
      filteredSequence.forEach((item)=>{
        item.activities.forEach((activity)=>{
          if(activity.delivery_order.id == delivery_id && activity.type == 'PICKUP' && activity.status.raw_type != 'COMPLETED'){
            isNotCompleted = true
          }
        })
      })
      return isNotCompleted
    },
    async handleClickItem(type, item, point) {
      this.selectedActivity = { ...item }
      this.selectedDeliveryOrder = this.selectedDos.find(
        (order) => order.id === item.delivery_order.id
      )

      const defaults = this.sequence.filter((item) => item.type === 'DEFAULT')

      const targetId = this.selectedDeliveryOrder.id

      const flat = defaults.flatMap((d) => d.activities)

      const lastActivities = defaults[defaults.length - 1].activities
      const isLastDefaults = lastActivities.some((a) => a.delivery_order.id === targetId)

      const targetIndex = flat.findIndex((a) => a.delivery_order.id === targetId)

      const isPrevDone = flat
        .slice(0, targetIndex)
        .some((a) => a.type === 'DROP' && a.status.raw_type === 'COMPLETED')

      this.isLastPoint = this.selectedDos.length <= 1 ? true : isLastDefaults && isPrevDone

      await this.$nextTick()

      if (type == 'remove') {
        this.$bvModal.show(
          `mc-modal-remove-order-${this.selectedActivity?.delivery_order.id}-${this.point.id}`
        )
      } else if (type == 'complete') {
        if (
          item.type === 'DROP' &&
          this.isPopNotCompleted(this.point.id, this.selectedActivity?.delivery_order.id)
        ) {
          this.$mcToast.error(toastErrors['complete-pod/pop-not-completed'])
          return
        }
        
        this.isSingleComplete = true
        this.$bvModal.show(`mc-modal-bulk-complete-${point.id}`)
      } else if (type == 'transfer') {
        this.$bvModal.show(
          `mc-modal-transfer-${this.selectedActivity?.delivery_order.id}-${this.point.id}`
        )
      } else if (type == 'view_proof') {
        this.$bvModal.show(`mc-modal-view-proof-${this.selectedActivity?.id}`)
      } else if (type == 'load_history') {
        this.$bvModal.show(`mc-modal-load-history-${this.selectedActivity?.id}`)
      } else if (type == 'cancel') {
        this.$bvModal.show(`mc-modal-cancel-order-${this.selectedActivity?.id}`)
      } else if (type == 'rescheduled') {
        this.$bvModal.show(`mc-modal-reschedule-order-${this.selectedActivity?.id}`)
      } else if (type == 'load_adjustment' || type == 'view_load') {
        const refName = 'complete-activity-modal-v2'
        this.$refs[refName].HandleCompleteActivity()
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return

      let classes = []

      const hash = `#${item?.delivery_order?.number}__${item.type}`
      if (this.$route.hash == hash) {
        classes.push('__highlight-row')
      }

      const deliveryOrder = this.selectedDos.find((order) => order.id === item.delivery_order.id)

      if (deliveryOrder && deliveryOrder.shipment_type === 'TRANSIT' && this.hasDoTransitError) {
        classes.push('__red-border-row')
      }

      return classes.join(' ')
    },
    getTarget(item) {
      const order = this.selectedDos.find((v) => v.id == item.delivery_order.id)
      if (!order) {
        return '-'
      }

      let result
      if (item.type === 'PICKUP') {
        if (order.expected_pickup_on) {
          const formatted = formatIndexDate(order.expected_pickup_on, 'DD MMM YYYY')
          result = formatted.date ? `${formatted.date} ${formatted.time}` : '-'
        }
      } else {
        if (order.expected_delivered_on) {
          const formatted = formatIndexDate(order.expected_delivered_on, 'DD MMM YYYY')
          result = formatted.date ? `${formatted.date} ${formatted.time}` : '-'
        }
      }

      return result
    },
    getQuantity(item) {
      const load = this.sequenceActLoad.find(s=>s.id === item.id)
      ?.delivery_order?.delivery_activity_loads
      if(!load) return null

      const isCompleted = item.status?.raw_type === "COMPLETED"

      return {
        qty: isCompleted ? load[0]?.actual_qty : load[0]?.planned_qty,
        name: load[0]?.product?.product_type?.name,
        uom: load[0]?.uom,
        length: load.length
      }
    },
    order(item) {
      const order = this.selectedDos.find((v) => v.id == item.delivery_order.id)
      return {
        shipment_number: order && order.shipment_number ? order.shipment_number : '-',
        reference_number:
          order && order.external_reference_number ? order.external_reference_number : '-',
        reference_number_internal: order && order.reference_number ? order.reference_number : '-',
        shipment_type:
          this.hasDoTransitError && order && order.shipment_type === 'TRANSIT' ? 'TRANSIT' : null,
      }
    },
    calculateSpan() {
      const { fleetTaskStatus, fleetTaskDetail } = this
      const isFinishedOrCanceled = ['Dibatalkan'].includes(fleetTaskStatus)
      const isFromVendorRequest = fleetTaskDetail?.is_from_vendor_request
      const hasVendor = fleetTaskDetail?.vendor?.id

      if (isFinishedOrCanceled || isFromVendorRequest) {
        return ['Perjalanan', 'Selesai'].includes(fleetTaskStatus) ? 4 : 3
      }

      return fleetTaskStatus !== 'Draf' && hasVendor ? 3 : 4
    },
    scrollToTransitError() {
      const errorRow = this.$el.querySelector('.__red-border-row')
      if (errorRow) {
        errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' })

        errorRow.classList.add('__highlight-transit-error')
        setTimeout(() => {
          errorRow.classList.remove('__highlight-transit-error')
        }, 2000)
      }
    },
    handleOpenViewProof(item) {
      this.selectedActivity = { ...item }
      this.$bvModal.show(`mc-modal-view-proof-${this.selectedActivity?.id}`)
    },
    handleBulkComplete(point) {
      for (const activity of point.activities??[]) {
        if (
          activity.type === 'DROP' &&
          this.isPopNotCompleted(point.id, activity?.delivery_order.id)
        ) {
          this.$mcToast.error(toastErrors['complete-pod/pop-not-completed'])
          return
        }
      }
      this.isSingleComplete = false
      this.$bvModal.show(`mc-modal-bulk-complete-${point.id}`)
    },
    canBulkComplete(point) {
      const isStarted = this.fleetTaskStatus === 'Perjalanan'
      const hasManyActivities = point?.activities?.length > 1
      const firstType = point?.activities?.[0]?.type
      const hasSameType = point?.activities?.every(a => a.type === firstType)
      const someActivityIsCompleted = point?.activities?.some(a => a.status?.raw_type === 'COMPLETED')
      return isStarted && hasManyActivities && hasSameType && !someActivityIsCompleted
    },
  },
}
</script>

<style lang="scss" scoped>
.__activity-footer > th {
  background: #ebecef;
  padding: 0;

  & td {
    padding: 12.5px 12px;
    background-color: transparent;
  }
}

:deep(.mc-activity-table) {
  margin-top: 10px;
  text-align: left;

  .th-movements {
    width: 30px;
  }

  .th-activity {
    width: 50px;
  }

  .th-shipment {
    width: 130px;
  }

  .th-customer {
    width: 500px;
  }

  .th-weight-volume,
  .th-reference .th-reference-internal {
    width: 100px;
  }

  .th-remove {
    width: 50px;
  }

  .th-do {
    width: 130px;
  }

  td {
    vertical-align: middle;
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .__completed {
    background-color: #429677;
  }

  .__not-completed {
    background-color: #99a1b1;
  }

  .more-icon {
    font-size: 20px;
  }
}

.__quantity-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.__quantity-detail {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.__quantity-name {
  max-width: 80px;
  color: #99a1b1;
  font-size: 12px;
}

.__quantity-count {
  padding: 2px 6px;
  color: white;
  background: #137c55;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.__remove-order-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  cursor: pointer;

  color: #f41d00;
  border: 1px solid #f41d00;
  padding: 2px;
  border-radius: 3px;

  i::before {
    font-size: 16px;
    color: #f41d00;
  }
}

.__activity_badge {
  padding: 4px 8px;
  color: white;
  border-radius: 3px;
  font-weight: 600;
  font-size: 11px;
  width: fit-content;
  line-height: 16px;

  &.__pickup {
    background: #1c74da;
  }

  &.__drop {
    background: #137c55;
  }
}

.__arrow-buttons {
  line-height: 8px;

  button {
    padding: 0;
    border: 0;
    background: none;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i::before {
      font-size: 18px;
    }
  }
}

.__table-container {
  overflow-x: auto;
  position: relative;
  margin-bottom: 20px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.__table-container-overlay::after {
  content: ' ';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(255, 255, 255, 0.5);
  z-index: 10;
}

.mc-title-transit_flag {
  font-style: italic;
  color: $red;
}

:deep(.icon-outline_file-text:before) {
  font-size: 20px !important;
}

:deep(.__highlight-row) {
  animation: blinker 0.5s linear 3;
  outline: 1px solid rgba($primary, 0.3);
  outline-offset: -1px;
}

:deep(.__red-border-row) {
  td {
    border-top: 1px solid $red;
    border-bottom: 1px solid $red;

    &:first-child {
      border-left: 1px solid $red;
    }

    &:last-child {
      border-right: 1px solid $red;
    }
  }
}

.toggle-activity {
  .completed-icon,
  .more-icon {
    font-size: 20px;
  }

  .completed-icon {
    &::before {
      color: $success;
    }
  }
}

:deep(#toggle-activity) {
  .dropdown-toggle {
    border: none;
    background-color: transparent;
    padding: 0;

    &:hover {
      opacity: 1;
    }
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .__danger {
    a {
      color: $red;
    }
  }
}

:deep(.dropdown-left .dropdown-menu.dropdown-menu) {
  top: 50% !important;
  transform: translateY(-50%) !important;
  left: auto !important;
  right: 100% !important;
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}

:deep(.__highlight-transit-error) {
  animation: transitErrorBlinker 0.5s linear 4;
  background-color: rgba(244, 29, 0, 0.1);
}

@keyframes transitErrorBlinker {
  0% {
    background-color: rgba(244, 29, 0, 0.1);
  }
  50% {
    background-color: rgba(244, 29, 0, 0.3);
  }
  100% {
    background-color: rgba(244, 29, 0, 0.1);
  }
}
</style>
